import { m } from '../../lib/motion.js';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/format.js';
import { useOnScreen } from '../../hooks/useOnScreen.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * A page section with the standard rhythm — 48px on mobile, 72px on desktop —
 * and the single entry animation the PRD allows: one fade-and-rise on first
 * scroll into view, 24px of travel, once only.
 */
export const Section = ({ children, className, bleed = false, as: Component = 'section', ...props }) => {
  const [ref, seen] = useOnScreen();
  const reduced = useReducedMotion();

  const MotionComponent = m[Component] ?? m.section;

  return (
    <MotionComponent
      ref={ref}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
      animate={seen ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: reduced ? 0.25 : 0.5, ease: [0.25, 1, 0.5, 1] }}
      className={cn('py-section lg:py-section-lg', className)}
      {...props}
    >
      <div className={cn(!bleed && 'mx-auto max-w-(--container-content) px-4 sm:px-6')}>{children}</div>
    </MotionComponent>
  );
};

/**
 * Section headings share one shape: a small gold eyebrow, then the title
 * centred between two hairlines, and an optional link out to the full listing
 * beneath it. Consistency here is most of what makes a long home page read as
 * one document.
 *
 * `align="start"` keeps the older left-aligned form for pages that sit a
 * heading beside other content rather than over a full-width grid.
 */
export const SectionHeading = ({
  eyebrow,
  title,
  description,
  action,
  eyebrowClassName,
  className,
  align = 'center',
}) => {
  if (align === 'start') {
    return (
      <header className={cn('mb-8 flex flex-wrap items-end justify-between gap-x-6 gap-y-3', className)}>
        <div className="max-w-xl">
          {eyebrow && (
            <p className={cn('text-2xs font-medium tracking-[0.14em] uppercase', eyebrowClassName ?? 'text-brand')}>
              {eyebrow}
            </p>
          )}
          <h2 className="mt-2 font-display text-2xl text-ink">{title}</h2>
          {description && <p className="mt-2 text-xs leading-relaxed text-muted">{description}</p>}
        </div>
        {action && <SectionAction action={action} />}
      </header>
    );
  }

  return (
    <header className={cn('mb-8 text-center lg:mb-10', className)}>
      {eyebrow && (
        <p className={cn('text-2xs font-medium tracking-[0.14em] uppercase', eyebrowClassName ?? 'text-brand')}>
          {eyebrow}
        </p>
      )}
      <div className="mt-2 flex items-center gap-4 sm:gap-6">
        <span aria-hidden="true" className="h-px flex-1 bg-mist" />
        <h2 className="shrink-0 font-display text-xl text-ink sm:text-2xl">{title}</h2>
        <span aria-hidden="true" className="h-px flex-1 bg-mist" />
      </div>
      {description && (
        <p className="mx-auto mt-3 max-w-2xl text-xs leading-relaxed text-muted">{description}</p>
      )}
      {action && (
        <div className="mt-3">
          <SectionAction action={action} />
        </div>
      )}
    </header>
  );
};

const SectionAction = ({ action }) => (
  <Link
    to={action.to}
    className="group inline-block shrink-0 text-2xs font-medium tracking-[0.12em] text-brand uppercase hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
  >
    {action.label}
    <span aria-hidden="true" className="ml-1.5 inline-block transition-transform group-hover:translate-x-0.5">
      →
    </span>
  </Link>
);

export default Section;
